import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileCheck2, FileWarning, Send, LogOut, FolderOpen, MessageCircle, FileText } from "lucide-react";
import { api, API, BACKEND, APP_TYPE_LABELS, DOC_TYPE_LABELS, fmtDDMMYYYY } from "../api";

const Header = ({ me, onLogout }) => (
  <header className="bg-forest text-cream border-b border-gold/30 sticky top-0 z-30">
    <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img src="/logo.png" alt="Elin's" className="h-10" />
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-gold-soft">Elin's Customer Portal</p>
          <p className="text-sm">Welcome, {me?.name?.split(" ")[0] || "there"}</p>
        </div>
      </div>
      <button onClick={onLogout} className="text-[11px] font-bold tracking-[0.14em] uppercase hover:text-gold inline-flex items-center gap-1.5"><LogOut size={14} /> Sign out</button>
    </div>
  </header>
);

export default function CustomerPortal({ me, onLogout }) {
  const [apps, setApps] = useState([]);
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [messageBody, setMessageBody] = useState("");
  const [messages, setMessages] = useState([]);

  const load = async () => {
    const a = await api.get(`${API}/applications`);
    setApps(a.data);
    if (a.data.length) {
      const first = a.data[0];
      const d = await api.get(`${API}/doc-requests?case_id=${first.id}`);
      setDocs(d.data);
      setActive(first);
      setAnswers(first.answers_draft || {});
      const m = await api.get(`${API}/messages?case_id=${first.id}`);
      setMessages(m.data);
    }
  };
  useEffect(() => { load(); }, []);

  const switchCase = async (app) => {
    setActive(app); setAnswers(app.answers_draft || {});
    const d = await api.get(`${API}/doc-requests?case_id=${app.id}`); setDocs(d.data);
    const m = await api.get(`${API}/messages?case_id=${app.id}`); setMessages(m.data);
  };

  const saveAnswers = async () => {
    try {
      await api.put(`${API}/applications/${active.id}/answers`, { answers });
      toast.success("Saved — you can return to this at any time");
    } catch (e) { toast.error("Could not save"); }
  };
  const submitAnswers = async () => {
    try {
      await api.post(`${API}/applications/${active.id}/answers/submit`);
      toast.success("Submitted — the agency will review next");
      load();
    } catch (e) { toast.error("Could not submit"); }
  };
  const upload = async (reqId, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`${API}/doc-requests/${reqId}/upload`, fd);
      toast.success("Uploaded"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const sendMessage = async () => {
    if (!messageBody.trim()) return;
    try {
      await api.post(`${API}/messages`, { case_id: active.id, scope: "customer", body: messageBody });
      setMessageBody("");
      const m = await api.get(`${API}/messages?case_id=${active.id}`); setMessages(m.data);
      toast.success("Message sent");
    } catch (e) { toast.error("Could not send"); }
  };

  if (!active) {
    return (
      <div className="min-h-screen bg-cream-3">
        <Header me={me} onLogout={onLogout} />
        <main className="max-w-5xl mx-auto px-5 py-10 text-center">
          <p className="text-sage">You don't have any active cases yet. Your agency contact will set one up for you.</p>
        </main>
      </div>
    );
  }

  const outstanding = docs.filter(d => d.status === "requested" || d.status === "replacement_required");

  return (
    <div className="min-h-screen bg-cream-3" data-testid="customer-portal">
      <Header me={me} onLogout={onLogout} />
      <main className="max-w-5xl mx-auto px-5 py-8 space-y-6">
        {apps.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {apps.map(a => (
              <button key={a.id} onClick={() => switchCase(a)} className={`px-3 py-1.5 rounded border text-[12px] whitespace-nowrap ${active.id === a.id ? "bg-forest text-cream border-forest" : "bg-white border-forest/20 text-forest hover:border-forest"}`}>{a.ref} · {APP_TYPE_LABELS[a.type]}</button>
            ))}
          </div>
        )}

        {/* Overview */}
        <section className="bg-white rounded-xl border border-forest/15 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold font-bold">Your case</p>
              <h1 className="font-serif text-2xl text-forest mt-1">{APP_TYPE_LABELS[active.type]} <span className="text-sage font-sans text-base">· {active.ref}</span></h1>
              <p className="text-sm text-ink/80 mt-3">{active.customer_status || "Your agency team is preparing your next step."}</p>
            </div>
            <div className="bg-gold/15 border border-gold/40 rounded-lg p-3 min-w-[220px]">
              <p className="text-[10px] tracking-[0.14em] uppercase font-bold text-gold-dark">Next action</p>
              <p className="text-sm text-forest mt-1">{active.next_action}</p>
              <p className="text-[11px] text-sage mt-2">Follow-up: {fmtDDMMYYYY(active.follow_up_date)}</p>
            </div>
          </div>
          <p className="text-[11px] text-sage mt-4">
            Our role is administrative. Any mortgage or finance advice and lender application is handled by your assigned broker partner.
          </p>
        </section>

        {/* Questionnaire */}
        <section className="bg-white rounded-xl border border-forest/15 p-6">
          <h2 className="font-serif text-xl text-forest flex items-center gap-2"><FileText size={18} /> Your questionnaire</h2>
          <p className="text-[12px] text-sage mt-1">Save as you go. Nothing is submitted until you tap Submit.</p>
          <QuestionsFor type={active.type} subtype={active.subtype} answers={answers} setAnswers={setAnswers} />
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={saveAnswers} data-testid="portal-save" className="px-4 py-2 border border-forest/25 rounded text-[12px] font-bold tracking-[0.14em] uppercase text-forest hover:border-forest">Save &amp; continue later</button>
            <button onClick={submitAnswers} data-testid="portal-submit" className="btn-gold">Submit for review</button>
          </div>
        </section>

        {/* Documents */}
        <section className="bg-white rounded-xl border border-forest/15 p-6">
          <h2 className="font-serif text-xl text-forest flex items-center gap-2"><FolderOpen size={18} /> Documents</h2>
          <p className="text-[12px] text-sage mt-1">Upload what we've asked for. Your files are private.</p>
          <ul className="divide-y divide-forest/10 mt-4">
            {docs.map((d) => (
              <li key={d.id} className="py-3 flex items-start justify-between gap-4" data-testid={`portal-doc-${d.doc_type}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-forest">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</p>
                  <p className="text-[12px] text-sage">{d.related_ref?.label} · {d.period || "—"}</p>
                  {d.instructions && <p className="text-[12px] text-ink/70 mt-1">{d.instructions}</p>}
                  {d.replacement_reason && <p className="text-[12px] text-rose-700 mt-1">Replacement needed: {d.replacement_reason}</p>}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-2">
                  <span className={`text-[10px] tracking-[0.14em] uppercase font-bold px-2 py-1 border rounded ${d.status === "uploaded" ? "bg-sky-50 text-sky-700 border-sky-200" : d.status === "agency_checked" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : d.status === "replacement_required" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{d.status.replace(/_/g, " ")}</span>
                  {(d.status === "requested" || d.status === "replacement_required") && (
                    <label className="inline-flex items-center gap-1 bg-forest text-cream text-[11px] font-bold tracking-[0.14em] uppercase px-3 py-1.5 rounded cursor-pointer hover:bg-forest-soft">
                      <Upload size={12} /> Upload
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={`portal-upload-${d.doc_type}`} onChange={(e) => e.target.files[0] && upload(d.id, e.target.files[0])} />
                    </label>
                  )}
                </div>
              </li>
            ))}
            {docs.length === 0 && <li className="py-6 text-center text-sm text-sage">Nothing requested yet.</li>}
          </ul>
          {outstanding.length > 0 && <p className="text-[11px] text-sage mt-4">{outstanding.length} outstanding of {docs.length}.</p>}
        </section>

        {/* Messages */}
        <section className="bg-white rounded-xl border border-forest/15 p-6">
          <h2 className="font-serif text-xl text-forest flex items-center gap-2"><MessageCircle size={18} /> Messages</h2>
          <ul className="space-y-2 mt-4 max-h-64 overflow-y-auto">
            {messages.filter(m => m.scope === "customer" && m.published).map(m => (
              <li key={m.id} className="bg-cream-3 border border-forest/10 rounded p-3">
                <p className="text-[11px] text-sage">{fmtDDMMYYYY(m.created_at)}</p>
                <p className="text-[13px] text-ink/80 whitespace-pre-wrap">{m.body}</p>
              </li>
            ))}
            {messages.filter(m => m.scope === "customer" && m.published).length === 0 && <li className="text-sm text-sage text-center py-4">No messages yet.</li>}
          </ul>
          <div className="mt-3 flex gap-2">
            <textarea rows={2} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Ask your agency a question…" className="flex-1 border border-forest/20 rounded px-3 py-2 text-[13px]" data-testid="portal-msg" />
            <button onClick={sendMessage} className="btn-gold self-end"><Send size={13} /> Send</button>
          </div>
        </section>
      </main>
    </div>
  );
}

/** Simple conditional questionnaire. Full configurable field library is editable
 *  by admins; this is the baseline shape saved as a map. */
function QuestionsFor({ type, subtype, answers, setAnswers }) {
  const set = (k, v) => setAnswers({ ...answers, [k]: v });
  const Row = ({ label, k, type: inputType = "text", hint, options, placeholder }) => (
    <label className="block">
      <span className="block text-[11px] font-bold tracking-[0.14em] uppercase text-forest/60 mb-1">{label}</span>
      {options ? (
        <select value={answers[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full border border-forest/20 rounded px-3 py-2 text-sm" data-testid={`q-${k}`}>
          <option value="">—</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : inputType === "textarea" ? (
        <textarea rows={3} value={answers[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} className="w-full border border-forest/20 rounded px-3 py-2 text-sm" data-testid={`q-${k}`} />
      ) : (
        <input type={inputType} value={answers[k] ?? ""} onChange={(e) => set(k, inputType === "number" ? Number(e.target.value) : e.target.value)} placeholder={placeholder} className="w-full border border-forest/20 rounded px-3 py-2 text-sm" data-testid={`q-${k}`} />
      )}
      {hint && <p className="text-[11px] text-sage mt-1">{hint}</p>}
    </label>
  );

  const common = (
    <>
      <Row label="Primary applicant name" k="applicant_name" />
      <Row label="Joint applicant name (if any)" k="joint_applicant_name" />
      <Row label="Dependants (number)" k="dependants" type="number" />
      <Row label="Employment status" k="employment_status" options={["Employed", "Self-employed", "Director of a limited company", "Retired", "Other"]} />
      <Row label="Monthly regular expenditure (£)" k="monthly_expenditure" type="number" hint="Rough figure is fine; broker will review." />
      <Row label="Existing borrowing and commitments" k="existing_borrowing" type="textarea" placeholder="Loans, credit cards, car finance, student loan…" />
    </>
  );

  if (type === "res_mortgage" || type === "btl_mortgage") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        {common}
        <Row label="Property value (£)" k="property_value" type="number" />
        <Row label="Deposit (£)" k="deposit_amount" type="number" />
        <Row label="Deposit source" k="deposit_source" type="textarea" placeholder="Savings / gift / sale of existing property…" />
        <Row label="Finance required (£)" k="finance_required" type="number" />
        <Row label="Timescale" k="timescale" options={["ASAP", "1-3 months", "3-6 months", "6+ months"]} />
        {type === "btl_mortgage" && (<>
          <Row label="Ownership" k="btl_ownership" options={["Individual", "Limited company"]} />
          <Row label="Expected monthly rent (£)" k="btl_rent" type="number" />
          <Row label="Existing portfolio summary" k="btl_portfolio" type="textarea" />
        </>)}
        {subtype && <p className="sm:col-span-2 text-[11px] text-sage">Application subtype: {subtype}.</p>}
      </div>
    );
  }
  if (type === "business_loan") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <Row label="Business legal name" k="biz_legal_name" />
        <Row label="Business structure" k="biz_structure" options={["Limited company", "Sole trader", "Partnership", "LLP"]} />
        <Row label="Industry" k="biz_industry" />
        <Row label="Trading start date" k="biz_trading_start" type="date" />
        <Row label="Turnover (last FY, £)" k="biz_turnover" type="number" />
        <Row label="Net profit (last FY, £)" k="biz_profit" type="number" />
        <Row label="Amount required (£)" k="amount_required" type="number" />
        <Row label="Preferred term (months)" k="preferred_term_months" type="number" />
        <div className="sm:col-span-2"><Row label="Purpose of borrowing" k="purpose" type="textarea" /></div>
        <div className="sm:col-span-2"><Row label="Existing business borrowing" k="biz_existing" type="textarea" /></div>
        <div className="sm:col-span-2"><Row label="Proposed security (if any)" k="biz_security" type="textarea" hint="For broker review only — we don't give advice." /></div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
      <div className="sm:col-span-2"><Row label="Tell us what you need" k="other_description" type="textarea" /></div>
    </div>
  );
}
